import "./finance-tab.scss";
import { P2Alert, P2Button } from "react-bootstrap-common-script";
import { Helpers } from "../../../../../utils/helper";
import { DownloadSvg } from "../../../../../assets/svg";
import { useGetSolicitorFinancialDetailsQuery, useGetSolicitorNotificationQuery } from "../../../../../redux/services/getSolicitor";
import { useAppSelector } from "../../../../../redux/hook";
import { useCallback } from "react";
import { LottieLoader } from "../../../../loader";
import { downloadFile } from "../../../../../utils/download-file";
import { Tab } from "../tabs-container/tabs-container";
import { PaymentCardIcon } from "../../../../../assets/svg";
import StageEInfoCard from "../../../../stage-e-info-card/stage-e-info-card";
import Separator from "../../../../separator/separator";
import { useSolicitorCaseDetailsContext } from "../../../../../context/solicitor-case-details-context";
import { SolicitorNotificationRequest } from "../../../../../models/classes/solicitor/SolicitorNotification.class";
import { SolicitorFinancialStatusEnum } from "../../../../../models/enum/solicitor-financial-status.enum";
import { API_Solicitor_Notification } from "../../../../../utils/api";
import { PolymerAxios } from "../../../../../utils/axios";

export function FinanceTab({ refetch }: Tab) {
  // States
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const { uen } = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);
  const { solicitorCaseSummaryData } = useSolicitorCaseDetailsContext();

  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");
  const scheduledCompletionDate = solicitorCaseSummaryData?.scheduled_completion_date ?? "";
  const afterScheduledCompletionDate = new Date() >= new Date(scheduledCompletionDate);

  const {
    data: financialDetailsData,
    isFetching: financialDetailsIsFetching,
    isError: financialDetailsIsError,
  } = useGetSolicitorFinancialDetailsQuery(
    { uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? "", application_uuid: applicationUuid ?? "", uen },
    { skip: !loginUser || !singpassToken || !applicationUuid }
  );

  const { solicitorNotificationRefetch } = useSolicitorCaseDetailsContext();

  // Functions
  const handleFileOnClick = useCallback(
    (key: string) => async () => {
      downloadFile(key, singpassToken);

      const request: SolicitorNotificationRequest = {
        financial_status: SolicitorFinancialStatusEnum.no_action,
      };

      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken, uen).patch(API_Solicitor_Notification, request, {
        params: { applicationUuid: applicationUuid },
      });

      solicitorNotificationRefetch();
    },
    [singpassToken]
  );

  if (financialDetailsIsFetching) {
    return <LottieLoader />;
  }

  if (financialDetailsIsError) {
    return <P2Alert variant="warning" text="An error occured when fetching financial details." style={{ marginLeft: 24, marginRight: 24 }} />;
  }

  return (
    <div>
      {financialDetailsData?.required_payment !== undefined &&
        financialDetailsData.required_payment.length > 0 &&
        afterScheduledCompletionDate && (
          <div>
            <div style={{ paddingLeft: 24, paddingRight: 24 }} className='mb-5'>
              <h2 className='polymer-finance-tab__title'>Required Payment</h2>
              <div className='mb-3'>
                <p>The following payments are due to HDB.</p>
              </div>
              {financialDetailsData.required_payment.map((requiredPayment) => {
                const sideLabel =
                  requiredPayment.owner_side === "CO" ? "Current owners" : "Proposed owners";

                return (
                  <div key={requiredPayment.owner_side} className='mb-4'>
                    <h3 className='polymer-finance-tab__title' style={{ fontSize: 20 }}>
                      {sideLabel}
                    </h3>
                    {requiredPayment.paid_on === "" ? (
                      <StageEInfoCard
                        variant='info'
                        cardLabel={'Amount to be paid'}
                        title={'$' + requiredPayment.amount}
                        description={"You pay on completion appointment day using cash, cashier's order and AXS, subject to standard payment limits."}
                        icon={<PaymentCardIcon />}
                      />
                    ) : (
                      <StageEInfoCard
                        variant='success'
                        cardLabel='Payment successful'
                        title={'$' + requiredPayment.amount}
                        paidOn={requiredPayment.paid_on ? requiredPayment.paid_on : ''}
                        paidBy={requiredPayment.paid_by ?? ''}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <Separator variant='solid' width={1} />
          </div>
        )}
      <div style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 20 }}>
        <h2 className='polymer-finance-tab__title'>Financial Information</h2>
        <div className='mb-3'>
          <p>HDB will prepare a financial plan outlining the payments due, calculated based on the tentative completion date.</p>
        </div>
        {financialDetailsData?.latest_financial_plan ? (
          <div className='polymer-finance-tab__item-container'>
            <span className='polymer-finance-tab__item-date'>
              Generated on {Helpers.formatDate(financialDetailsData.latest_financial_plan.submitted_date, 'long')}
            </span>
            <P2Button
              onClick={handleFileOnClick(financialDetailsData.latest_financial_plan.key)}
              endIcon={<DownloadSvg strokeColor='#0A676D' />}
              style={{ fontSize: 18, color: '#0A676D', textDecoration: 'underline' }}>
              Financial details
            </P2Button>
          </div>
        ) : (
          <P2Alert
            variant='warning'
            text='There is no actions required at the moment, will let you know if there are any updates on the application.'
          />
        )}
      </div>
    </div>
  );
}
